export default class Collapsable {
  /**
   * Define heights of elements matched by `selector` so they can be collapsed by css height animation
   * Add / remove `collapsedClass` class to element matched by `selector` on click on its child matched by `buttonSelector`
   * - If element is collapsed by default and contains `expandOnlyClass` class, it's not allowed to collapse again when opened
   * - If an element with an id matching to the `buttonSelector` hash exists, default browser scrolling behaviour will apply,
   *   but it can be offsetted by providing the `offsetElement` property to the class when initializing
   *
   * @param {Int} transitionTime - Collapse animation duration in milliseconds
   * @param {String} selector - Collapsable element selector
   * @param {String} buttonSelector - Collapsable child button selector
   * @param {String} collapsedClass - Class name when element is collapsed
   * @param {String} expandOnlyClass - Class name to make element expand only (must be collapsed by default)
   */
  constructor(
    transitionTime = 500,
    selector = '.collapsable',
    buttonSelector = '.collapsable-btn',
    collapsedClass = 'is-collapsed',
    expandOnlyClass = 'collapse-expand-only'
  ) {
    this.transitionTime = transitionTime;
    this.selector = selector;
    this.buttonSelector = buttonSelector;
    this.collapsedClass = collapsedClass;
    this.expandOnlyClass = expandOnlyClass;
    this.offsetElement = null; // Can be provided to offset the default scrolling behaviour

    this.bindEvents();
  }

  /**
   * Bind events
   */
  bindEvents() {
    this.onResize = this.onResize.bind(this);
    this.onBeforePrint = this.onBeforePrint.bind(this);
    this.destroy = this.destroy.bind(this);
    this.onAfterPrint = this.onAfterPrint.bind(this);
    this.collapse = this.collapse.bind(this);
    window.addEventListener('load', this.defineHeights.bind(this));
    window.addEventListener('custom-resize', this.onResize);
    window.matchMedia('print').addListener(this.onBeforePrint);
    window.addEventListener('beforeprint', this.destroy);
    window.addEventListener('afterprint', this.onAfterPrint);
    document.querySelectorAll(this.selector).forEach(elt => {
      const buttons = elt.querySelectorAll(this.buttonSelector);
      if (buttons) {
        for (const button of buttons) {
          button.addEventListener('click', this.collapse);
        }
      }
    });
  }

  /**
   * Collapse / open
   * @param {MouseEvent} e
   */
  collapse(e) {
    e.preventDefault();
    const parent = e.currentTarget.closest(this.selector);
    parent.classList.toggle(this.collapsedClass);

    const hash = e.currentTarget.hash ? e.currentTarget.hash.substring(1) : null;
    setTimeout(() => {
      if (hash && document.getElementById(hash)) {
        window.location.hash = hash;
        if (this.offsetElement !== null) {
          setTimeout(() => {
            window.scrollTo(0, window.scrollY - this.offsetElement.getBoundingClientRect().height);
          }, 250);
        }
      }
      if (parent.classList.contains(this.expandOnlyClass)) {
        this.destroy();
      }
    }, this.transitionTime);
  }

  /**
   * Define heights of all collapsable elements
   */
  defineHeights() {
    document.querySelectorAll(this.selector).forEach(elt => this.defineElementHeight(elt));
  }

  /**
   * Define height of one element
   * @param {HTMLElement} elt
   */
  defineElementHeight(elt) {
    let collapse = false;
    if (elt.classList.contains(this.collapsedClass)) {
      elt.classList.remove(this.collapsedClass);
      collapse = true;
    }
    elt.style.height = 'auto';
    elt.style.height = `${elt.getBoundingClientRect().height}px`;
    if (collapse) {
      elt.classList.add(this.collapsedClass);
    }
  }

  /**
   * Unbind events
   * Reset heights of elements by group
   */
  destroy() {
    window.removeEventListener('custom-resize', this.onResize);
    window.matchMedia('print').removeListener(this.onBeforePrint);
    window.removeEventListener('beforeprint', this.destroy);
    window.removeEventListener('afterprint', this.onAfterPrint);
    document.querySelectorAll(this.selector).forEach(elt => {
      elt.querySelector(this.buttonSelector).removeEventListener('click', this.collapse);
      elt.style.height = 'auto';
    });
  }

  /**
   * CustomResize window.custom-resize handler
   * @param {CustomEvent} e
   */
  onResize(e) {
    if (e.detail.width) {
      /** Define heights */
      this.defineHeights();
    }
  }

  /**
   * Before print event listener
   * @param {MediaQueryList}
   */
  onBeforePrint(mql) {
    if (mql.matches) {
      /** Reset heights */
      this.destroy();
    }
  }

  /**
   * After print event listener
   */
  onAfterPrint() {
    /** Define heights */
    this.defineHeights();
    /** Bind events */
    this.bindEvents();
  }
}



// WEBPACK FOOTER //
// ./scripts/util/Collapsable.js